import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { FileText, RefreshCw, Download, Satellite, Calculator } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _currentQuarter = () => {
  const d = new Date();
  return { year: d.getFullYear(), quarter: Math.floor(d.getMonth() / 3) + 1 };
};

/**
 * IFTA Report — per-truck mileage roll-up for one quarter, with a clear
 * source tag on every row (ELD vs estimated). No auto-filing — this is
 * an aggregation building block for the finance team.
 */
const IftaReport = () => {
  const initial = useMemo(() => _currentQuarter(), []);
  const [year, setYear] = useState(initial.year);
  const [quarter, setQuarter] = useState(initial.quarter);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/ifta/report?year=${year}&quarter=${quarter}`,
        { withCredentials: true },
      );
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      setError(typeof msg === 'string' ? msg : 'Unable to load IFTA report');
    } finally {
      setLoading(false);
    }
  }, [year, quarter]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExport = () => {
    if (!data?.rows) return;
    const header = 'Truck,VIN,ELD Provider,ELD Miles,Estimated Miles,Source,Primary Miles\n';
    const csv = data.rows.map((r) => [
      JSON.stringify(r.truck_label || ''),
      JSON.stringify(r.vin || ''),
      r.eld_provider || '',
      r.eld_miles ?? '',
      r.manual_estimated_miles ?? '',
      r.source || '',
      r.primary_miles ?? '',
    ].join(',')).join('\n');
    const blob = new Blob([header + csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ifta-${year}-Q${quarter}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const years = [initial.year - 2, initial.year - 1, initial.year, initial.year + 1];

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="ifta-report-page">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1 text-xs font-semibold uppercase tracking-widest text-slate-500">
              <FileText className="w-3.5 h-3.5" /> IFTA
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Quarterly Mileage Report
            </h1>
            <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Per-truck miles for the quarter with a clear source tag. Use the ELD numbers where available; estimated is a fallback from delivered-load miles.
            </p>
          </div>
          <button
            type="button"
            onClick={handleExport}
            disabled={!data?.rows?.length}
            data-testid="ifta-export"
            className="inline-flex items-center gap-2 text-sm bg-[#002FA7] text-white px-3 py-2 rounded-md font-semibold hover:bg-[#00247D] disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-4 mb-6 flex items-center gap-3 flex-wrap">
          <label className="text-xs text-slate-500 uppercase tracking-widest font-semibold">Year</label>
          <select
            data-testid="ifta-year"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            className="px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-white"
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <label className="text-xs text-slate-500 uppercase tracking-widest font-semibold ml-4">Quarter</label>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4].map((q) => (
              <button
                key={q}
                data-testid={`ifta-q-${q}`}
                onClick={() => setQuarter(q)}
                className={`px-3 py-1.5 text-sm rounded-md font-semibold ${
                  quarter === q
                    ? 'bg-[#002FA7] text-white'
                    : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
                }`}
              >Q{q}</button>
            ))}
          </div>
          <button
            type="button"
            onClick={fetchReport}
            aria-label="Refresh"
            data-testid="ifta-refresh"
            className="ml-auto p-2 rounded-md hover:bg-slate-100 text-slate-500"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 py-6">Loading…</div>
        ) : error ? (
          <div data-testid="ifta-error" className="text-xs text-red-600 py-4">{error}</div>
        ) : !data?.rows?.length ? (
          <div
            data-testid="ifta-empty"
            className="text-center py-12 text-xs text-slate-500 border border-dashed border-slate-200 rounded-md"
          >
            No trucks yet for this quarter.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              <SourceStat
                label="ELD miles"
                value={data.totals.eld_miles}
                icon={Satellite}
                tone="brand"
                testid="ifta-total-eld"
              />
              <SourceStat
                label="Estimated miles"
                value={data.totals.estimated_miles}
                icon={Calculator}
                tone="slate"
                testid="ifta-total-estimated"
              />
              <SourceStat
                label="Grand total"
                value={data.totals.eld_miles + data.totals.estimated_miles}
                icon={FileText}
                tone="brand"
                testid="ifta-total-grand"
              />
            </div>
            <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    <th className="py-2 px-3">Truck</th>
                    <th className="py-2 px-3">ELD Provider</th>
                    <th className="py-2 px-3 text-right">ELD Miles</th>
                    <th className="py-2 px-3 text-right">Estimated Miles</th>
                    <th className="py-2 px-3">Source</th>
                    <th className="py-2 px-3 text-right">Primary Miles</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.truck_asset_id} data-testid={`ifta-row-${r.truck_asset_id}`} className="border-b border-slate-100">
                      <td className="py-2 px-3">
                        <div className="font-semibold text-slate-900">{r.truck_label || 'Truck'}</div>
                        {r.vin && <div className="text-[10px] font-mono text-slate-400">{r.vin}</div>}
                      </td>
                      <td className="py-2 px-3 capitalize text-slate-700">{r.eld_provider || '—'}</td>
                      <td className="py-2 px-3 text-right font-mono">
                        {typeof r.eld_miles === 'number' ? r.eld_miles.toLocaleString() : '—'}
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-slate-500">
                        {typeof r.manual_estimated_miles === 'number' ? r.manual_estimated_miles.toLocaleString() : '—'}
                      </td>
                      <td className="py-2 px-3">
                        {r.source === 'eld' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-[#002FA7]/10 text-[#002FA7]">
                            <Satellite className="w-3 h-3" /> ELD
                          </span>
                        ) : r.source === 'estimated' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800">
                            <Calculator className="w-3 h-3" /> Estimated
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right font-mono font-bold">
                        {typeof r.primary_miles === 'number' ? r.primary_miles.toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-slate-400 mt-3 italic">
              Estimated rows are fallback data based on delivered-load ``total_miles``. Reconcile with fuel receipts before filing.
            </p>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

const SourceStat = ({ label, value, icon: Icon, tone, testid }) => {
  const tones = {
    brand: 'border-[#002FA7]/30 bg-[#002FA7]/5 text-[#002FA7]',
    slate: 'border-slate-200 bg-white text-slate-700',
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-md border p-4 ${tones[tone] || tones.slate}`}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">{label}</div>
      </div>
      <div className="text-2xl font-bold mt-1 text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
        {(value || 0).toLocaleString()} <span className="text-sm font-normal text-slate-500">mi</span>
      </div>
    </div>
  );
};

export default IftaReport;
